import { useContext, useRef, useEffect} from 'react';
import { CallClient } from '@azure/communication-calling';
import { AzureCommunicationTokenCredential } from '@azure/communication-common';
import { ACSContext } from '../contexts/ACSContext'
import { CMSContext } from '../contexts/CMSContext';
import useCMS from './useCMS';
import { useTranslation } from 'react-i18next';
import useLogger from './useLog';

const useACS = () => {
  const {t} = useTranslation();
  const {GetTokenForUser } = useCMS();
  const {buildings} = useContext(CMSContext);
  const {LogInfo, LogWarning, LogError, LogVerbose} = useLogger();

  const {callAgent, setCallAgent, 
          setCallClient,
          activeCall, setActiveCall,
          incomingCall, setIncomingCall,
          setDeviceManager,
          setRemoteVideoStream, 
          setVideoAvailable,
          setSelectedDevice,
          setRemoteName
        } = useContext(ACSContext);
  
  const buildingsRef = useRef();
  useEffect(() => {
          buildingsRef.current = buildings;
      }, [buildings]);

  //finds the device in the building data based on the ACSID, used to display caller name
  const findDeviceByACSID = (acsId) => {
    LogInfo('findDeviceByACSID called for ' + acsId);

    if(!buildingsRef.current|| buildingsRef.current.length === 0){
      LogError('findDeviceByACSID - no buildings found', buildingsRef.current);
      return null;
    }

    for (const building of buildingsRef.current) {
      for (const flat of building.flats) {
        for (const device of flat.devices) {
          if (device.ACSID === acsId) {
            const response = {deviceType: device.devicetype, displayName: device.displayName, flatNumber: flat.flatname,  familyName: flat.familyName};
            //if more than one building, add building name to the response
            if (buildingsRef.current.length > 1) {
              response.buildingName = building.title;
            }
            LogInfo("device found: " + JSON.stringify(response));
            return response;
          }
        }
      }
    }
    LogInfo("device not found");
    return null;
  };
      
  
  const handleIncomingCallEvent = (_incomingCall) => {
    LogInfo('incoming call event fired');
    try{
      handleIncomingCall(_incomingCall.incomingCall);
    }
    catch (error) {
      LogError('Failed to handle incoming call event');
//      console.error(error);
    }
    LogInfo('Incoming call event handled');
  };

  const handleIncomingCallEnded = () => {
    LogInfo('Incoming call ended', incomingCall);
    setIncomingCall(null);
    };
    
  const handleIncomingCall = (_incomingCall) => {
    LogInfo('Handling incoming call in useACS');
    if (activeCall || incomingCall){ 
      LogInfo('existing call in progress, rejecting incoming call');
      _incomingCall.reject();
      return;
    }
    LogInfo('subscribe to call ended');
    //subscribe to call ended event and keep incoming call for UI to handle
    _incomingCall.on('callEnded', () => {
      handleIncomingCallEnded();
    });
    LogInfo('set incoming call');
    setIncomingCall(_incomingCall);
    setRemoteName(findDeviceByACSID(_incomingCall.callerInfo.identifier.communicationUserId));
    
    LogInfo('Incoming call set in useACS:');
  };

  //when incoming call is accepted in the UI
  const acceptCall = async () => {
    LogInfo('Accepting call');
    if (!incomingCall) {
        LogError('No incoming call');
        return;
    }
  
    const call = await incomingCall.accept();
    LogInfo('Call accepted');
    setIncomingCall(null);//clear incoming call ref
    setActiveCall(call);
    call.on('stateChanged', () =>callStateChanged(call));
    // Inspect the call's current remote participants and subscribe to them.
    call.remoteParticipants.forEach(remoteParticipant => {
        subscribeToRemoteParticipant(remoteParticipant);
    });
  
    //TODO: do we need the below? duplicate of the above? 
    // Subscribe to the call's 'remoteParticipantsUpdated' event to be
    // notified when new participants are added to the call or removed from the call.
    call.on('remoteParticipantsUpdated', e => {
        LogInfo('Remote participants updated',e);
        // Subscribe to new remote participants that are added to the call.
        e.added.forEach(remoteParticipant => {
            subscribeToRemoteParticipant(remoteParticipant)
        });
        // Unsubscribe from participants that are removed from the call
        e.removed.forEach(remoteParticipant => {
            LogInfo('Remote participant removed from the call.');
        });
    });
  }

  const subscribeToRemoteParticipant = (remoteParticipant) => {
  try {
    LogInfo(`Remote participant state: ${remoteParticipant.state}`);
    remoteParticipant.videoStreams.forEach(remoteVideoStream => {
        if(remoteVideoStream.mediaStreamType === 'Video'){ //we only care about video (other stream is screen sharing)
            LogInfo(`Subscribing to Remote participant video stream. isAvailable: ${remoteVideoStream.isAvailable}, isReceiving: ${remoteVideoStream.isReceiving}, type: ${remoteVideoStream.mediaStreamType}`);
            //subscribe to the remoteVideoStream's 'isAvailableChanged' event.
            remoteVideoStream.on("isAvailableChanged", () => {
                LogInfo(`Remote participant video stream isAvailable: ${remoteVideoStream.isAvailable}`);
                if(remoteVideoStream.isAvailable){
                    setRemoteVideoStream(remoteVideoStream);
                    setVideoAvailable(true);
                }
                else{
                    setRemoteVideoStream(null);
                    setVideoAvailable(false);
                }
            });
        }
    });
  } catch (error) {
    LogError(error);
  }
  }
  
  const endCall = () => {
    LogInfo('Rejecting call');
    setRemoteName(null);
    if (incomingCall){
        incomingCall.reject();
        setIncomingCall(null);
    }
    if (activeCall){
        activeCall.hangUp();
        setActiveCall(null);
    }
  }
  
  const handleCallEnded = (call) => {
  LogInfo('Call ended:', call);
  //TODO: move to UI
  //outgoingRingtone.pause();

  setActiveCall(null);
  setIncomingCall(null);
  }
  
  const startCall = async (device) => {
    try{
        if(!callAgent){ 
            LogError('Call agent not initialized');
            return;
        }
        LogInfo('Starting call to ' + device);
        setSelectedDevice(device); //YD: not sure this is needed
        setRemoteName(findDeviceByACSID(device.ACSID)); 

        const call = await callAgent.startCall([{ communicationUserId: device.ACSID }]);
        setActiveCall(call); //store the active call
        
        LogInfo('Call to ' +device.deviceid + ' started. state: ' +call.state , {callId: call.id});
  
        call.on('stateChanged', () =>callStateChanged(call));
    }
    catch (error) {
        LogError('Failed to start call: ' +  error);
        setRemoteName(null); 
    }
  };
  
 
  const callStateChanged = (call) => {
    try{
    LogInfo('Call state changed to ' +  call.state + ' for call ' + call.id);
  
    switch (call.state) {
        case 'Connecting':
            LogInfo('Call connecting', {callId: call.id});
            break;
        case 'Disconnected':
            handleCallEnded(call);
            setRemoteVideoStream(null);
            setVideoAvailable(false);
            break;
        case 'Connected':
            LogInfo('Call Connected', {callId: call.id});
              break;
            default:
              break;
    }
  }
  catch (error) {
    LogError('Failed to handle call state change: ' +  error);
  }};

  const initializeACS = async (token, userId) => {
    try {
      LogInfo('initializeACS called for user:', userId);
      if (callAgent) {
        // ACS agent is already initialized
        LogWarning('ACS agent already initialized');
        return true;
      }
      else{
        LogInfo('ACS agent not initialized');
      }
      //ensure there's an existing token
      if(!token){
        LogWarning('initializeACS - no token found, calling getTokenForUser');
        token = await GetTokenForUser(userId); 
      }

      LogInfo('initializeACS - token exists, create callClient ');
      const callClient = new CallClient();
      const tokenCredential = new AzureCommunicationTokenCredential(
        { 
          token: token, 
          refreshProactively: true,
          tokenRefresher: async () => {
            LogInfo('useACS is refreshing token for user:', userId);
            return GetTokenForUser(userId); // Ensure this returns Promise<string> with the new token
          } 
        }
      );
      LogInfo('initializeACS - create call agent ');
      //create call agent, initially as a local variable because React's setCallAgent is async.
      const localCallAgent = await callClient.createCallAgent(tokenCredential, {displayName: t('c4uguard')});
      
      //(should the follow be moved to a useEffect on callAgent?)
      LogInfo('initializeACS - get device manager');
      const deviceManager = await callClient.getDeviceManager();
//    deviceManager.askDevicePermission({ video: true }); we do not send guard video
      deviceManager.askDevicePermission({ audio: true });

      LogInfo('subscribe to incoming call event');
      //remove any previous listeners if exist, to prevetn duplicates
      try{
        localCallAgent.off('incomingCall', handleIncomingCallEvent);
        LogInfo('Removed previous incoming call event listener');
      }
      catch (error) {
        LogVerbose('Failed to remove previous incoming call event listener:' + error);
      }
      localCallAgent.on('incomingCall', handleIncomingCallEvent);
      LogInfo('Incoming call event listener added');

      setCallAgent(localCallAgent);
      setCallClient(callClient);
      setDeviceManager(deviceManager);
    } catch (error) {
      LogError('Failed to initialize ACS:', error);
      return false;
    }
    return true;
  };


  return {initializeACS, startCall, acceptCall, endCall};
};




export default useACS;